import { useEffect, useState } from "react";

import { getProethicaTestDescriptor } from "@constants/demo/proethica-tests";
import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { useProethicaNextModule } from "@features/dashboard/hooks/useProethicaNextModule";
import { useSession, useSessionStore } from "@features/session";
import { useProethicaSessionStore } from "@features/session/hooks/useProethicaSessionStore";
import { useAppTheme } from "@hooks/useAppTheme";
import {
	Box,
	CardContainer,
	DonutScore,
	ScrollContainer,
	useDevice,
} from "@memorang/ui";
import { ProethicaTestBadge } from "components/ProethicaTestBadge";
import { router } from "expo-router";
import { Button, ProgressBar, Text } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
	PROETHICA_PASS_PERCENTAGE,
	computeProethicaTestScore,
	isProethicaTestPassed,
} from "../../helpers/proethica-test-report";

// Score-only report for pre/post tests: mirrors the module report chrome
// (completed progress bar + status header + single result card + footer) but
// with no analytics, distribution, or topic breakdown. The post-test footer
// branches on the 80% pass gate - failing offers a fresh retake, passing
// persists the pass and unlocks review.
const ProethicaTestReport = () => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const insets = useSafeAreaInsets();

	const sessionItems = useSessionStore((state) => state.sessionItems);
	const itemStates = useSessionStore((state) => state.itemStates);
	const proethicaTestKind = useSessionStore((state) => state.proethicaTestKind);
	const resetSessionStore = useSessionStore((state) => state.resetSessionStore);
	const sessionId = useSessionStore((state) => state.id);
	const updateInReview = useSessionStore((state) => state.updateInReview);
	const gradeItemsForReview = useSessionStore(
		(state) => state.gradeItemsForReview,
	);

	const setPreTestCompleted = useProethicaSessionStore(
		(state) => state.setPreTestCompleted,
	);
	const setPostTestPassed = useProethicaSessionStore(
		(state) => state.setPostTestPassed,
	);

	const { handleStartProethicaTest } = useSession();
	const { nextModuleLabel, handleContinueToNextModule, isStartingNextModule } =
		useProethicaNextModule(proethicaTestKind ?? undefined);
	const [retaking, setRetaking] = useState(false);

	const descriptor = getProethicaTestDescriptor(proethicaTestKind);
	const { numCorrect, numTotal, percentage } = computeProethicaTestScore(
		sessionItems,
		itemStates,
	);

	const isPretest = proethicaTestKind === "pretest";
	const isPostTest = proethicaTestKind === "posttest";
	const passed = isProethicaTestPassed(percentage);

	useEffect(() => {
		if (isPretest) {
			setPreTestCompleted(true);
		}
		if (isPostTest && passed) {
			setPostTestPassed(true);
		}
	}, [isPretest, isPostTest, passed, setPreTestCompleted, setPostTestPassed]);

	const handleGoHome = () => {
		resetSessionStore();
		router.replace("/(protected)/(tabs)/home");
	};

	const handleRetake = async () => {
		setRetaking(true);
		try {
			await handleStartProethicaTest({ testId: "posttest" });
		} finally {
			setRetaking(false);
		}
	};

	const handleReviewAnswers = () => {
		const reviewItems = sessionItems as unknown as Parameters<
			typeof updateInReview
		>[1];
		gradeItemsForReview(reviewItems);
		updateInReview(true, reviewItems);
		router.push({
			pathname: "/(protected)/session/[id]/review",
			params: { id: String(sessionId) },
		});
	};

	const typeLabel = descriptor?.typeLabel ?? "Test";
	const copy = isPretest
		? {
				title: "Here's your starting point",
				description:
					"This is your baseline. You'll see your progress when you take the post-test.",
				scoreTitle: "Baseline score",
			}
		: passed
			? {
					title: "You passed",
					description: `You're past the ${PROETHICA_PASS_PERCENTAGE}% pass mark. You can now review every answer and its feedback.`,
					scoreTitle: "Your score",
				}
			: {
					title: "Just short this time",
					description: `You need ${PROETHICA_PASS_PERCENTAGE}% to pass. Go back through the modules and retake the post-test.`,
					scoreTitle: "Your score",
				};

	const primaryCta = isPostTest
		? passed
			? {
					label: "Review answers",
					icon: "clipboard-text-search-outline",
					onPress: handleReviewAnswers,
					loading: false,
				}
			: {
					label: "Retake post-test",
					icon: "refresh",
					onPress: handleRetake,
					loading: retaking,
				}
		: {
				label: nextModuleLabel
					? `Continue to ${nextModuleLabel}`
					: "Continue to next module",
				icon: undefined,
				onPress: handleContinueToNextModule,
				loading: isStartingNextModule,
			};

	return (
		<ScrollContainer
			contentContainerStyle={{
				paddingTop: 16,
				paddingBottom: 32 + insets.bottom,
				paddingHorizontal: 16,
			}}
		>
			<Box width="100%" maxWidth={560} alignSelf="center" style={{ gap: 32 }}>
				<Box flexDirection="row" alignItems="center">
					<Box flex={1}>
						<ProgressBar
							progress={1}
							color={theme.colors.primary}
							style={{
								height: 8,
								borderRadius: 4,
								backgroundColor: theme.colors.surfaceVariant,
							}}
						/>
					</Box>
					<Box
						width={44}
						height={44}
						borderRadius={22}
						backgroundColor={theme.colors.primary}
						alignItems="center"
						justifyContent="center"
						style={{ marginLeft: -3 }}
					>
						<Icon name="check" size={24} color={theme.colors.white} />
					</Box>
				</Box>

				<Box alignItems="center" style={{ gap: 12 }}>
					<ProethicaTestBadge
						kind={proethicaTestKind ?? "posttest"}
						size={72}
					/>
					<Box alignItems="center" style={{ gap: 6 }}>
						<Text
							variant="labelLarge"
							style={{
								color: theme.colors.success.main,
								fontWeight: "800",
								letterSpacing: 1,
							}}
						>
							{`${typeLabel.toUpperCase()} COMPLETE`}
						</Text>
						<Text
							variant={isMobile ? "headlineSmall" : "headlineMedium"}
							style={{
								color: theme.colors.onSurface,
								fontWeight: "800",
								textAlign: "center",
							}}
							accessibilityRole="header"
						>
							{copy.title}
						</Text>
						<Text
							variant="bodyLarge"
							style={{
								color: theme.colors.onSurfaceVariant,
								textAlign: "center",
								maxWidth: 480,
							}}
						>
							{copy.description}
						</Text>
					</Box>
				</Box>

				<CardContainer
					title={copy.scoreTitle}
					subtitle={`Across all ${numTotal} questions`}
					icon="chart-box-outline"
				>
					<Box
						paddingHorizontal={16}
						paddingVertical={28}
						alignItems="center"
						style={{ gap: 16 }}
					>
						<DonutScore
							distribution={[percentage, 100 - percentage]}
							score={percentage}
							variant="progress"
							radius={64}
							innerRadius={50}
							customColorScale={[
								theme.colors.primary,
								theme.colors.progress.none,
							]}
							customChildren={
								<Text
									variant="headlineSmall"
									style={{ color: theme.colors.onSurface, fontWeight: "800" }}
								>
									{`${numCorrect}/${numTotal}`}
								</Text>
							}
						/>
						<Text
							variant="titleMedium"
							style={{ color: theme.colors.onSurfaceVariant }}
						>
							{`${percentage}% correct`}
						</Text>
					</Box>
				</CardContainer>

				<Box
					flexDirection={isMobile ? "column" : "row"}
					alignItems="stretch"
					style={{ gap: 12 }}
				>
					<Box flex={isMobile ? undefined : 1}>
						<Button
							mode="contained"
							icon={primaryCta.icon}
							loading={primaryCta.loading}
							disabled={primaryCta.loading}
							onPress={primaryCta.onPress}
							contentStyle={{ height: 48 }}
						>
							{primaryCta.label}
						</Button>
					</Box>
					<Button
						mode="outlined"
						icon="home-outline"
						onPress={handleGoHome}
						contentStyle={{ height: 48 }}
					>
						Go home
					</Button>
				</Box>
			</Box>
		</ScrollContainer>
	);
};

export default ProethicaTestReport;
